import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import {
    IMediaAssetModel,
    MEDIA_TYPE,
    MediaAssetService,
    checkUploadableFiles,
    formatMediaDuration,
    formatMediaResolution,
    isVideoAttachment,
    mediaThumbnailUrl,
} from "../../Services/MediaAssetService";
import "./MediaManager.scss";

/** What was stored about the file itself, as far as it is known: "PNG · 1920 × 1080", "MP4 · 1280 × 720 · 0:42". */
const mediaMetaLabel = (asset: IMediaAssetModel): string =>
    [asset.fileExtension?.toUpperCase(), formatMediaResolution(asset), formatMediaDuration(asset)]
        .filter(Boolean)
        .join(" · ");

const MEDIA_STATUS_READY = "READY";

interface MediaManagerProps {
    knowledgeArticleUuid: string;
    /** The article's media. The page owns (and reloads) this list — it needs it for its sections anyway, so
     *  this component never fetches it a second time. Not needed with `showList={false}`. */
    mediaAssets?: IMediaAssetModel[];
    /** Something was uploaded or deleted: the page reloads its media. */
    onChanged?: (deletedAssetUuid?: string) => void;
    /** Hide this component's own upload button — used when the trigger lives elsewhere (e.g. a header
     *  icon) and calls {@link MediaManagerHandle.openUploadMenu} via a ref instead. */
    hideUploadButton?: boolean;
    /** Hide the attachment list, keeping only the (hidden) file input mounted — lets a card that renders its
     *  own list still upload from a header icon. */
    showList?: boolean;
}

export interface MediaManagerHandle {
    openUploadMenu: (event: React.SyntheticEvent) => void;
}

/**
 * Upload plain downloadable FILE attachments, and manage (view/delete) every attachment on the article —
 * including IMAGE/VIDEO/SLIDESHOW section items and TEXT-section inline photos, which are uploaded from
 * their own section editor but still need a single place to find/delete them from. Shared between the
 * full editor and the inline "manage from the reader view" flow.
 */
const MediaManager = forwardRef<MediaManagerHandle, MediaManagerProps>(
    ({ knowledgeArticleUuid, mediaAssets = [], onChanged, hideUploadButton = false, showList = true }, ref) => {
        const { t } = useTranslation();
        const mediaAssetService = useMemo(() => new MediaAssetService(), []);
        const fileInputRef = useRef<HTMLInputElement>(null);

        const [uploading, setUploading] = useState(false);
        const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

        useImperativeHandle(ref, () => ({
            openUploadMenu: () => fileInputRef.current?.click(),
        }));

        const handleFileSelected = async (file: File | null) => {
            if (!file) {
                return;
            }
            // The backend recognises a video among the attachments and treats it as one — same limits apply.
            const { violations } = await checkUploadableFiles([file]);
            if (violations.length > 0) {
                toast.error(t(violations[0]) as string);
                return;
            }
            setUploading(true);
            const loadingToastId = toast.loading(t("common.saving") as string);
            try {
                await mediaAssetService.uploadMediaAsset(knowledgeArticleUuid, MEDIA_TYPE.FILE, file);
                onChanged?.();
                toast.update(loadingToastId, { type: "success", render: t("common.saved") as string, isLoading: false, autoClose: 1500 });
            } catch (error) {
                toast.update(loadingToastId, {
                    type: "error",
                    render: resolveApiErrorMessage(error, "knowledgeBase.form.mediaUploadError", t),
                    isLoading: false,
                    autoClose: 3000,
                });
            } finally {
                setUploading(false);
            }
        };

        const handleDelete = async (assetUuid: string) => {
            setDeletingUuid(assetUuid);
            const loadingToastId = toast.loading(t("common.saving") as string);
            try {
                await mediaAssetService.deleteMediaAsset(assetUuid);
                onChanged?.(assetUuid);
                toast.update(loadingToastId, { type: "success", render: t("common.saved") as string, isLoading: false, autoClose: 1500 });
            } catch (error) {
                toast.update(loadingToastId, {
                    type: "error",
                    render: resolveApiErrorMessage(error, "knowledgeBase.form.mediaDeleteError", t),
                    isLoading: false,
                    autoClose: 3000,
                });
            } finally {
                setDeletingUuid(null);
            }
        };

        return (
            <div className="media-manager">
                <div className="media-manager__upload-row">
                    {!hideUploadButton && (
                        <Button
                            type="button"
                            icon={uploading ? "pi pi-spinner pi-spin" : "pi pi-upload"}
                            className="media-manager__upload-button-icon"
                            text
                            rounded
                            disabled={uploading}
                            title={t("knowledgeBase.form.uploadMedia") as string}
                            onClick={() => fileInputRef.current?.click()}
                        />
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={(e) => {
                            handleFileSelected(e.target.files?.[0] ?? null);
                            e.target.value = "";
                        }}
                    />
                </div>
                {showList && mediaAssets.length > 0 && (
                    <ul className="media-manager__list">
                        {mediaAssets.map((asset) => (
                            <li key={asset.uuid} className="media-manager__list-item">
                                {mediaThumbnailUrl(asset) ? (
                                    <img className="media-manager__list-thumb" src={mediaThumbnailUrl(asset)} alt="" />
                                ) : (
                                    <i
                                        className={`pi ${
                                            asset.mediaType === MEDIA_TYPE.VIDEO || isVideoAttachment(asset) ? "pi-video" : "pi-file"
                                        }`}
                                    />
                                )}
                                <span className="media-manager__list-name">{asset.originalFilename}</span>
                                {mediaMetaLabel(asset) && (
                                    <span className="media-manager__list-meta">{mediaMetaLabel(asset)}</span>
                                )}
                                {asset.processingStatus !== MEDIA_STATUS_READY && (
                                    <span className="media-manager__list-status">
                                        {t(`knowledgeBase.mediaStatus.${asset.processingStatus}`)}
                                    </span>
                                )}
                                <Button
                                    type="button"
                                    icon={deletingUuid === asset.uuid ? "pi pi-spinner pi-spin" : "pi pi-trash"}
                                    text
                                    disabled={deletingUuid !== null}
                                    onClick={() => handleDelete(asset.uuid)}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }
);

export default MediaManager;
